import { createAsyncThunk } from "@reduxjs/toolkit";
import { baseUrl, headersDefault } from ".";
import { announceEntitlementRequired, isEntitlementRequired } from "../../services/entitlementPrompt";

/**
 * Fetch paginated list of streaming events.
 * Params: { page, limit, include_past_events, audiences }
 */
export const getStreamingEvents = createAsyncThunk(
  "streaming/getStreamingEvents",
  async ({ page = 1, limit = 15, include_past_events = true, audiences } = {}) => {
    const params = new URLSearchParams();
    params.append("page", page);
    params.append("limit", limit);
    params.append("include_past_events", include_past_events ? 1 : 0);
    if (audiences) {
      audiences.forEach((a) => params.append("audiences[]", a));
    }

    const url = `${baseUrl}/api/v1/streaming/events/list?${params.toString()}`;

    const response = await fetch(url, {
      headers: headersDefault(),
    });

    if (!response.ok) {
      throw new Error(response.statusText ?? "GenericError");
    }

    const data = await response.json();
    // data = { data: [Event], meta: {...} }
    return { ...data, page };
  }
);

/**
 * Fetch a single streaming event by ID.
 *
 * Uses the native XMLHttpRequest to bypass the global fetch interceptor
 * which treats 403 as an auth failure. For streaming events, 403 means
 * "access not granted" (paid/premium content), not "invalid token".
 */
/**
 * Register for a streaming event (take a seat).
 * Used for upcoming events — subscribes the user for push notifications.
 * POST /api/v1/streaming/events/{eventId}/seat
 */
export const registerForEvent = createAsyncThunk(
  "streaming/registerForEvent",
  async ({ eventId }) => {
    const response = await fetch(
      `${baseUrl}/api/v1/streaming/events/${eventId}/seat`,
      { method: "POST", headers: headersDefault() }
    );
    if (!response.ok) throw new Error(response.statusText ?? "GenericError");
    return await response.json();
  }
);

export const getStreamingEventDetail = createAsyncThunk(
  "streaming/getStreamingEventDetail",
  async ({ id }) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("GET", `${baseUrl}/api/v1/streaming/events/${id}/fetch`);

      const headers = headersDefault();
      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      xhr.onload = () => {
        try {
          const data = JSON.parse(xhr.responseText);
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(data);
          } else if (xhr.status === 403) {
            // 403 = access denied (paid/premium), not auth failure.
            // Reject so the page falls back to route state from the list.
            // Premium Lite: the backend says why — XHR bypasses the global
            // fetch/axios interceptors, so announce the popup here.
            if (isEntitlementRequired(xhr.status, data)) {
              announceEntitlementRequired(data);
            }
            reject(new Error("AccessDenied"));
          } else if (xhr.status === 404) {
            // 404 = this Diretta does not exist for this member: gone, or
            // limited to FREE editions they are not (or no longer) in.
            // Unlike 403 there is nothing to upsell, and the list entry the
            // page could fall back on is stale.
            reject(new Error("NotFound"));
          } else {
            reject(new Error(xhr.statusText || "GenericError"));
          }
        } catch {
          reject(new Error("ParseError"));
        }
      };
      xhr.onerror = () => reject(new Error("NetworkError"));
      xhr.send();
    });
  }
);

